/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/RoleBasedRouter.tsx
 * PURPOSE: TenantUserRole-based routing — replaces legacy email-to-position map
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 04 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * Sprint 53 (S53-003): Replace USER_POSITION_MAP (19 hardcoded email entries)
 * with TenantUserRole-based routing from JWT/auth store.
 * CTO Condition G2: RoleBasedRouter replacement.
 *
 * @business-context
 * - Domain: Role-based navigation, Vietnamese F&B platform
 * - Impact: All users — determines post-login dashboard
 * - Stakeholders: All tenant users
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: TenantUserRole enum-based routing (DD-0002 §5)
 * - Dependencies: React 18, useAuthStore (userRole field)
 *
 * @cross-references
 * - Design: docs/02-design/THREE-TIER-ROLE-DESIGN.md §5
 * - ADR: docs/02-design/18-adrs/06-decisions/DD-0002-three-tier-role-architecture.md §5
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import type { TenantUserRole } from '../types/auth';

/**
 * TenantUserRole -> dashboard path mapping per DD-0002 §5.
 * Replaces legacy USER_POSITION_MAP (19 hardcoded email entries).
 */
const ROLE_DASHBOARD_MAP: Record<TenantUserRole, string> = {
  TENANT_ADMIN: "/tenant-admin/dashboard",
  TENANT_OWNER: "/dashboard/chairman",
  CORPORATE_MANAGER: "/dashboard/manager",
  BRANCH_MANAGER: "/dashboard/manager",
  SHIFT_MANAGER: "/dashboard/manager",
  HEAD_CHEF: "/dashboard/staff",
  CHEF: "/dashboard/staff",
  WAITER: "/dashboard/staff",
  CASHIER: "/dashboard/staff",
  KITCHEN_STAFF: "/dashboard/staff",
  DELIVERY_STAFF: "/dashboard/staff",
  CLEANER: "/dashboard/staff",
  TRAINEE: "/dashboard/staff",
};

export const RoleBasedRouter: React.FC = () => {
  const { isAuthenticated, userRole } = useAuthStore();

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Route by TenantUserRole from auth store
  if (userRole && userRole in ROLE_DASHBOARD_MAP) {
    const dashboard = ROLE_DASHBOARD_MAP[userRole as TenantUserRole];
    return <Navigate to={dashboard} replace />;
  }

  // Fallback: staff dashboard
  return <Navigate to="/dashboard/staff" replace />;
};

/**
 * Get dashboard path for a given TenantUserRole.
 * Used by Login.tsx and SelectTenant.tsx for post-login navigation.
 */
export const getDashboardPath = (role: string): string => {
  return ROLE_DASHBOARD_MAP[role as TenantUserRole] || "/dashboard/staff";
};

/**
 * Check if user has a specific capability (DD-0002 §7 vocabulary).
 * Reads from auth store capabilities array set during login.
 */
export const hasCapability = (capability: string): boolean => {
  const { capabilities } = useAuthStore.getState();
  return capabilities.includes(capability);
};

export default RoleBasedRouter;
